import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiRefreshCw, FiFilter, FiDownload, FiPhone, FiCheckCircle, FiXCircle, FiSearch } from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminAPI, vtpassAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const AirtimeTransactions = () => {
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchPhone, setSearchPhone] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [successFilter, setSuccessFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const sourceOptions = useMemo(
    () => [
      { value: 'all', label: 'All sources' },
      { value: 'registration_bonus', label: 'Registration bonus' },
      { value: 'referral_bonus', label: 'Referral bonus' },
      { value: 'subscription_commission', label: 'Subscription commission' },
      { value: 'airtime_purchase', label: 'Airtime purchase' },
      { value: 'admin_retry', label: 'Admin retry' },
      { value: 'test', label: 'Test' },
      { value: 'unknown', label: 'Unknown' }
    ],
    []
  );

  const [rechargePhone, setRechargePhone] = useState('');
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeNetwork, setRechargeNetwork] = useState('');
  const [rechargeLoading, setRechargeLoading] = useState(false);

  const handleRecharge = async () => {
    try {
      const phone = rechargePhone.trim();
      if (!phone || !rechargeAmount.trim()) return;
      const amt = parseInt(rechargeAmount, 10);
      if (Number.isNaN(amt) || amt < 50) {
        showError('Amount must be at least 50 NGN');
        return;
      }
      if (phone.length < 10) {
        showError('Phone number must be at least 10 digits');
        return;
      }
      setRechargeLoading(true);
      const network = rechargeNetwork.trim() ? rechargeNetwork.trim().toLowerCase() : null;
      await vtpassAPI.purchaseAirtime(phone, amt, network);
      setRechargePhone('');
      setRechargeAmount('');
      setRechargeNetwork('');
      await fetchTransactions();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to initiate recharge';
      showError(msg);
    } finally {
      setRechargeLoading(false);
    }
  };

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 20 };
      if (searchPhone) params.phone = searchPhone;
      if (referralCode) params.referralCode = referralCode;
      if (sourceFilter && sourceFilter !== 'all') params.source = sourceFilter;
      if (successFilter && successFilter !== 'all') params.success = successFilter === 'success';
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await adminAPI.getAirtimeTransactions(params);
      const data = response.data;
      const items = data?.data || data?.items || [];
      const pagination = data?.pagination || {};
      setTransactions(items);
      setTotalPages(pagination.pages || 1);
    } catch (error) {
      showError('Failed to load airtime transactions');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchPhone, referralCode, sourceFilter, successFilter, startDate, endDate, showError]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const resetFilters = () => {
    setSearchPhone('');
    setReferralCode('');
    setSuccessFilter('all');
    setSourceFilter('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Airtime Transactions</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manual recharge and logs of airtime purchases and rewards via VTPass.</p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-6">
          <h2 className="text-sm font-medium text-slate-900 mb-3">Manual Recharge</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
            <div className="lg:col-span-3">
              <label className="block text-xs font-medium text-slate-500 mb-1">Phone number</label>
              <input
                type="tel"
                placeholder="08012345678"
                value={rechargePhone}
                onChange={(e) => setRechargePhone(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
              />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">Amount (NGN)</label>
              <input
                type="number"
                placeholder="100"
                value={rechargeAmount}
                onChange={(e) => setRechargeAmount(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
              />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">Network</label>
              <select
                value={rechargeNetwork}
                onChange={(e) => setRechargeNetwork(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
              >
                <option value="">Auto-detect</option>
                <option value="mtn">MTN</option>
                <option value="airtel">Airtel</option>
                <option value="glo">Glo</option>
                <option value="9mobile">9mobile</option>
              </select>
            </div>
            <div className="lg:col-span-2">
              <button
                type="button"
                onClick={handleRecharge}
                disabled={rechargeLoading || !rechargePhone.trim() || !rechargeAmount.trim()}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:pointer-events-none"
              >
                {rechargeLoading ? 'Processing…' : 'Recharge'}
              </button>
            </div>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-0 max-w-xs">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by phone"
              value={searchPhone}
              onChange={(e) => { setSearchPhone(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
            />
          </div>
          <input
            type="text"
            placeholder="Referral code"
            value={referralCode}
            onChange={(e) => { setReferralCode(e.target.value); setCurrentPage(1); }}
            className="w-full sm:w-auto min-w-0 max-w-xs px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
          />
          <select
            value={successFilter}
            onChange={(e) => { setSuccessFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
          >
            <option value="all">All statuses</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
          >
            {sourceOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
          />
          <div className="flex gap-2">
            <button type="button" onClick={resetFilters} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
              <FiRefreshCw className="h-4 w-4" />
              Reset
            </button>
            <button type="button" onClick={fetchTransactions} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
              <FiFilter className="h-4 w-4" />
              Apply
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiPhone className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No transactions found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Phone</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Network</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Source</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Request ID</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Transaction ID</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Referral Code</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Referrer</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700 whitespace-nowrap">Mode</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {transactions.map((tx, idx) => (
                    <tr key={tx._id || idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{tx.createdAt ? new Date(tx.createdAt).toLocaleString() : '—'}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <FiPhone className="h-3.5 w-3.5 text-slate-400" />
                          {tx.phone}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">₦{Number(tx.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{String(tx.serviceID || '').toUpperCase() || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{tx.source || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${tx.success ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                          {tx.success ? <FiCheckCircle className="h-3.5 w-3.5" /> : <FiXCircle className="h-3.5 w-3.5" />}
                          {tx.success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap font-mono text-xs">{tx.requestId || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap font-mono text-xs">{tx.transactionId || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{tx.referralCodeUsed || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{tx.referrerName || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{tx.vtpassMode || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between px-2">
            <span className="text-xs text-slate-500">Page {currentPage} of {totalPages}</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Previous</button>
              <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Next</button>
            </div>
          </div>
        )}

        <div className="mt-4">
          <button type="button" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900">
            <FiDownload className="h-4 w-4" />
            Export (coming soon)
          </button>
        </div>
      </div>
    </div>
  );
};

export default AirtimeTransactions;
